export const BUFFER_MINUTES = 15;

export type TBufferShift = {
  shiftId: number;
  start: Date;
  end: Date;
  checkedIn?: boolean;
};

export type TBufferedShift = TBufferShift & {
  effectiveEnd: Date;
  hasBuffer: boolean;
};

const toTime = (value: Date | string | number) => new Date(value).getTime();

/**
 * For each shift in a volunteer's schedule, add BUFFER_MINUTES to the end
 * unless the next shift (by start time) begins exactly when this one ends.
 */
export function withBuffer(shifts: TBufferShift[]): TBufferedShift[] {
  const sorted = [...shifts].sort(
    (a, b) => toTime(a.start) - toTime(b.start) || a.shiftId - b.shiftId
  );

  return sorted.map((shift, index) => {
    const next = sorted[index + 1];
    const isBackToBack =
      next !== undefined && toTime(shift.end) === toTime(next.start);
    const hasBuffer = !isBackToBack;
    const effectiveEnd = hasBuffer
      ? new Date(toTime(shift.end) + BUFFER_MINUTES * 60 * 1000)
      : new Date(shift.end);

    return {
      ...shift,
      start: new Date(shift.start),
      end: new Date(shift.end),
      effectiveEnd,
      hasBuffer,
    };
  });
}

export function getBufferedShift(
  shiftId: number,
  shifts: TBufferShift[]
): TBufferedShift | undefined {
  return withBuffer(shifts).find((shift) => shift.shiftId === shiftId);
}

export function getEffectiveEnd(
  shiftId: number,
  shifts: TBufferShift[]
): Date | undefined {
  return getBufferedShift(shiftId, shifts)?.effectiveEnd;
}

export type TSumBufferedHoursOptions = {
  checkedInOnly?: boolean;
  /** Compare against wall-clock end (not effective end). Defaults to now. */
  pastOnly?: boolean;
  now?: Date;
};

/**
 * Sum effective durations for a volunteer's shifts. Used for live hours display.
 */
export function sumBufferedHours(
  shifts: TBufferShift[],
  options: TSumBufferedHoursOptions = {}
): number {
  const { checkedInOnly = false, pastOnly = false, now = new Date() } = options;
  const buffered = withBuffer(shifts);
  const nowMs = now.getTime();

  const totalMs = buffered.reduce((sum, shift) => {
    if (checkedInOnly && !shift.checkedIn) return sum;
    if (pastOnly && toTime(shift.end) >= nowMs) return sum;
    return sum + (shift.effectiveEnd.getTime() - toTime(shift.start));
  }, 0);

  const hours = totalMs / (1000 * 60 * 60);
  return Math.round(hours * 100) / 100;
}

/**
 * Project buffer impact of adding `candidate` to the volunteer's current signups.
 * Replaces an existing entry with the same shiftId if present.
 */
export function projectWithCandidate(
  current: TBufferShift[],
  candidate: TBufferShift
): TBufferedShift[] {
  const withoutDuplicate = current.filter(
    (shift) => shift.shiftId !== candidate.shiftId
  );
  return withBuffer([...withoutDuplicate, candidate]);
}

export function getBufferRemovedFromNeighbors(
  current: TBufferShift[],
  candidate: TBufferShift
): TBufferedShift[] {
  const before = withBuffer(current);
  const after = projectWithCandidate(current, candidate);
  const afterById = new Map(after.map((shift) => [shift.shiftId, shift]));

  return before.filter((shift) => {
    if (shift.shiftId === candidate.shiftId) return false;
    const next = afterById.get(shift.shiftId);
    return shift.hasBuffer && next !== undefined && !next.hasBuffer;
  });
}
